// Ticket #59 — horario de atención de las sucursales, desde la fuente única.
//
// Hasta acá el sitio leía `dealerships.opening_hours`, un JSONB que se editaba
// en Configuración → Sucursales del admin y que era un modelo paralelo al que
// la automotora usa de verdad para agendar. Medido sobre la base: de las 8
// sucursales que tenían los dos cargados, 7 publicaban acá un horario distinto
// del que tenían en su agenda.
//
// La fuente única es `goauto_sched.dealership_hours` (Agenda → Configuración →
// Horario). El sitio consulta con la clave `anon`, que no tiene SELECT sobre
// esa tabla ni sobre su vista, así que el acceso va por la RPC
// `fn_dealership_public_hours` (SECURITY DEFINER, definida en la migración
// 20260928120000 del admin). La RPC devuelve sólo lo publicable: no expone
// cupos, pasos ni buffers.

import { supabase } from './supabase';

/** Una fila de horario tal como la devuelve `fn_dealership_public_hours`. */
export interface PublicDayHours {
  weekday: number; // 0 = domingo … 6 = sábado
  is_open: boolean;
  open_time: string | null; // 'HH:MM:SS'
  close_time: string | null;
  break_start?: string | null;
  break_end?: string | null;
}

interface RpcRow extends PublicDayHours {
  dealership_id: number;
}

export type PublicHoursByDealership = Record<number, PublicDayHours[]>;

/**
 * Agrupa las filas por sucursal. Los días sin fila NO se rellenan: esa ausencia
 * es la que significa "sin declarar" (ver OpeningHours). Rellenar acá
 * convertiría "sin declarar" en "cerrado".
 */
export const groupPublicHours = (rows: RpcRow[] | null): PublicHoursByDealership => {
  const out: PublicHoursByDealership = {};
  for (const row of rows || []) {
    const id = Number(row.dealership_id);
    if (!Number.isFinite(id)) continue;
    (out[id] ||= []).push(row);
  }
  return out;
};

/**
 * Horario publicable de todas las sucursales del tenant, o de una sola.
 *
 * Ante un error devuelve vacío y el bloque de horario no se pinta: es un dato
 * de una tarjeta de contacto, no algo por lo que valga la pena tumbar la
 * página. Y NO cae al JSONB viejo — un fallback silencioso sería volver a tener
 * dos fuentes.
 */
export const getDealershipHours = async (
  clientId: string | number,
  dealershipId?: number | null,
): Promise<PublicHoursByDealership> => {
  try {
    const { data, error } = await supabase.rpc('fn_dealership_public_hours', {
      p_client_id: Number(clientId),
      p_dealership_id: dealershipId ?? null,
    });
    if (error) throw error;
    return groupPublicHours(data as RpcRow[] | null);
  } catch (error) {
    console.error('Error fetching dealership hours:', error);
    return {};
  }
};
